import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true, error: null, invite: null });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: "Missing invitation token", invite: null });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/admins/verify-invite/${encodeURIComponent(token)}`);
        setState({ loading: false, error: null, invite: data });
      } catch (err) {
        setState({ loading: false, error: formatApiError(err), invite: null });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/admins/accept-invite", { token, password });
      setDone(true);
      toast.success("Account activated — you can now log in");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505] text-zinc-100">
      <div className="hidden lg:flex relative border-r border-zinc-800 bg-[#0A0A0A] grid-bg">
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Risk Intelligence</div>
            <div className="mt-2 text-3xl font-mono font-semibold tracking-tight">RISK_INTEL_SYSTEM</div>
          </div>
          <div className="space-y-4 font-mono text-xs text-zinc-500">
            <p className="leading-relaxed">You have been invited to join as an administrator. Set your password to activate the account.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm" data-testid="accept-invite-page">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Accept Invitation</div>
          <h1 className="text-3xl font-mono font-semibold tracking-tight mt-1">Set Password</h1>

          {state.loading && (
            <div className="mt-6 text-zinc-400 font-mono text-sm">VERIFYING_INVITATION…</div>
          )}

          {!state.loading && state.error && (
            <div className="mt-6 border border-red-500/40 bg-red-500/5 rounded-sm p-4 space-y-3">
              <div className="flex items-center gap-2 text-red-400 font-mono">
                <XCircle className="h-5 w-5" /> Invitation invalid
              </div>
              <div className="text-sm text-zinc-400 font-mono" data-testid="invite-error">{state.error}</div>
              <Button className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono w-full" onClick={() => nav("/login")}>Go to Login</Button>
            </div>
          )}

          {!state.loading && state.invite && !done && (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-4 space-y-1">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Invitee</div>
                <div className="font-mono text-white text-sm">{state.invite.name}</div>
                <div className="font-mono text-zinc-400 text-xs">{state.invite.email}</div>
                {state.invite.invited_by && (
                  <div className="text-[11px] font-mono text-zinc-500 mt-2 pt-2 border-t border-zinc-800">
                    Invited by <span className="text-zinc-300">{state.invite.invited_by}</span>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">New password</Label>
                <Input
                  data-testid="new-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono"
                  placeholder="min 6 characters"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Confirm password</Label>
                <Input
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono"
                  autoComplete="new-password"
                />
              </div>
              <Button
                data-testid="activate-account-btn"
                type="submit"
                disabled={busy}
                className="w-full rounded-sm bg-white text-black hover:bg-zinc-200 font-mono tracking-tight"
              >
                {busy ? "ACTIVATING…" : "ACTIVATE ACCOUNT"}
              </Button>
            </form>
          )}

          {done && (
            <div className="mt-6 border border-emerald-500/40 bg-emerald-500/5 rounded-sm p-4 space-y-3" data-testid="invite-done">
              <div className="flex items-center gap-2 text-emerald-400 font-mono">
                <CheckCircle2 className="h-5 w-5" /> Account activated
              </div>
              <p className="text-sm font-mono text-zinc-400">You can now log in with <span className="text-white">{state.invite?.email}</span> and the password you just set.</p>
              <Button className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono w-full" onClick={() => nav("/login")}>Go to Login</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
